/**
 * Glossary terms — single source of truth for /glossary page,
 * llms.txt definitions, and JSON-LD DefinedTermSet.
 *
 * Writing rules (per docs/seo-geo-playbook.md §4.2):
 *   - Each `definition` is a self-contained 60–120 char paragraph
 *     that can be lifted out by an LLM and still make sense.
 *   - Brand canonical terms ONLY: Hotline / Caller / Responder /
 *     OPC / 数字分身 / Marketplace. Synonyms forbidden.
 */

export type GlossaryTerm = {
  /** URL-safe id; used for in-page anchor + DefinedTerm @id. */
  id: string;
  /** Canonical Chinese / brand label. */
  term: string;
  /** Optional English / engineering alias. */
  alias?: string;
  /** 60–120 字自包含定义（首段） */
  definition: string;
  /** 1-2 句补充：与近义概念的边界、典型场景、为什么这样命名 */
  context?: string;
  /** 同一术语集内的相关条目 id */
  relatedIds?: string[];
  /** 分组，供页面分块渲染 */
  group: "core" | "role" | "marketplace" | "engineering" | "console" | "billing-governance";
};

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    id: "hotline",
    term: "Hotline",
    alias: "热线 / 一根专线",
    definition:
      "Hotline 是 CALL ANYTHING 协议里的能力单元——一段把身份、计费、审批、可观测、路由全部固化在协议里的标准化契约。它不是 API、不是 MCP server、不是 Skill，而是把这些接入方式统一成「Agent 可发现、可调用、可结算」的产品形态。",
    context:
      "一根 Hotline 同时可以暴露为 MCP Server / OpenAPI / SKILL.md，三者只是接入面；产品本体是 Hotline。",
    relatedIds: ["caller", "responder", "marketplace", "result-package"],
    group: "core",
  },
  {
    id: "caller",
    term: "Caller",
    alias: "调用方 / Agent 端",
    definition:
      "Caller 是发起 Hotline 调用的一方，通常是某个 AI Agent 或 Agent 团队。Caller 不需要为每根 Hotline 写 client 代码，只需通过协议拉取 catalog、按统一 result_package 结构解读返回，把外部能力像系统调用一样并入 Agent runtime。",
    relatedIds: ["responder", "hotline", "result-package", "caller-console"],
    group: "role",
  },
  {
    id: "responder",
    term: "Responder",
    alias: "提供方 / 数字分身的承载体",
    definition:
      "Responder 是 Hotline 背后承接调用的一方，绝大多数情况下是一位独立运营的超级个体（OPC）或一支小型团队。Responder 负责把私有专长包装成 Hotline，由 supervisor 守护进程持续接收调用、跑业务逻辑、回写结果。",
    relatedIds: ["caller", "hotline", "opc", "supervisor"],
    group: "role",
  },
  {
    id: "opc",
    term: "OPC",
    alias: "One-Person Company / 超级个体",
    definition:
      "OPC（One-Person Company，超级个体）是 CALL ANYTHING 第一受众：把私域专长打包成可被 Agent 7×24 调用、按次结算、自动入账的数字分身的个体。它不是独立开发者，也不是自由职业，而是用协议而不是公司形态来对外提供服务。",
    context:
      "OPC 与 indie hacker / freelancer / digital worker 都不同：它的产品是 Hotline，分销渠道是 Marketplace，结算路径在协议内。",
    relatedIds: ["responder", "digital-twin", "hotline", "marketplace"],
    group: "core",
  },
  {
    id: "digital-twin",
    term: "数字分身",
    alias: "Digital Twin (in OPC sense)",
    definition:
      "数字分身指 OPC 把自身专长封装为协议化能力后形成的「7×24 在线、可被 Agent 直接调用、按次自动结算」的对外服务实体。它的载体是一根或多根 Hotline，运行在本地 supervisor 上，对外只暴露契约，不暴露源码。",
    relatedIds: ["opc", "responder", "hotline", "supervisor"],
    group: "core",
  },
  {
    id: "marketplace",
    term: "Marketplace",
    alias: "公开 Hotline 目录",
    definition:
      "Marketplace 是 callanything.xyz 上的公开 Hotline 目录：任何符合协议的 Agent 都能在这里发现 Hotline、查看 responder 与版本、跳转拉取 catalog 并直接发起调用。它不是应用市场，不是 API 商店，是协议级的发现层。",
    relatedIds: ["hotline", "caller", "responder"],
    group: "marketplace",
  },
  {
    id: "supervisor",
    term: "Supervisor",
    alias: "client supervisor / 守护进程",
    definition:
      "Supervisor 是 client 仓库里运行在 Caller / Responder 本机的守护进程，负责挂载 Hotline、维护会话、转发 HTTP 调用、记录 ops 事件。它是协议在本地的具象化执行体，Console 原型也是连接 supervisor 来呈现状态。",
    relatedIds: ["caller", "responder", "hotline", "result-package"],
    group: "engineering",
  },
  {
    id: "hotline-id",
    term: "Hotline ID",
    alias: "namespaced hotline identifier",
    definition:
      "Hotline ID 是一根 Hotline 的全局命名，形如 `local.delegated-execution.workspace-summary.v1`：scope（local/marketplace）+ namespace + hotline-name + version。Caller 通过它精确锁定要调用的能力与版本。",
    relatedIds: ["hotline", "result-package", "catalog"],
    group: "engineering",
  },
  {
    id: "result-package",
    term: "Result Package",
    alias: "协议化返回包",
    definition:
      "Result Package 是 CALL ANYTHING 协议规定的统一返回结构：包含 status / hotline_id / output / timing / responder_id 等字段。所有 Hotline 都返回同一种 result_package，Agent 只学一次解析逻辑，可调用任意 Hotline。",
    relatedIds: ["hotline", "caller", "hotline-id"],
    group: "engineering",
  },
  {
    id: "catalog",
    term: "Catalog",
    alias: "本机可调用 Hotline 列表",
    definition:
      "Catalog 是 supervisor 暴露给 Caller 的 Hotline 清单 API（如 `GET /catalog/hotlines`）。Caller 通过 catalog 决定 Agent 当前可调用的能力集合；Marketplace 上的发现行为，本质就是 catalog 的公网视图。",
    relatedIds: ["caller", "marketplace", "hotline-id"],
    group: "engineering",
  },
  {
    id: "template-bundle",
    term: "Template Bundle",
    alias: "Hotline 模板包",
    definition:
      "Template Bundle 是 Responder 发布 Hotline 的最小描述包：summary、input_schema、output_schema、风险说明、示例 IO 与适配器（process / http）。Marketplace 审核与 Caller 接入都以 template bundle 为依据。",
    relatedIds: ["responder", "hotline", "marketplace"],
    group: "engineering",
  },
  {
    id: "approval-policy",
    term: "Approval Policy",
    alias: "审批策略",
    definition:
      "Approval Policy 是 Caller Console 上为某根 Hotline 配置的人工审批规则：按风险标签、调用频次、输入特征触发人工 review。它把 Agent 从「全自动调用」过渡到「关键调用人工把关」，是合规与信任的协议化抓手。",
    relatedIds: ["caller-console", "hotline", "caller"],
    group: "console",
  },
  {
    id: "caller-console",
    term: "Caller Console",
    alias: "Agent 团队管控台",
    definition:
      "Caller Console 是 Agent 团队侧的协议管控台：浏览 / 收藏 Hotline、配置审批策略、查看调用日志与计费、订阅版本变更。它面向 Agent 团队，回答「我的 Agent 都在调用什么、花了多少、是否合规」。",
    relatedIds: ["caller", "approval-policy", "hotline"],
    group: "console",
  },
  {
    id: "responder-console",
    term: "Responder Console",
    alias: "OPC 经营台",
    definition:
      "Responder Console 是 OPC 侧的协议经营台：上下架 Hotline、调整定价、查看调用与收入、回滚版本、处理 Caller 反馈。它面向超级个体，回答「我的数字分身今天接了多少单、赚了多少、要不要扩容或迭代」。",
    relatedIds: ["responder", "opc", "hotline", "marketplace"],
    group: "console",
  },
  {
    id: "call-credit",
    term: "Call Credit",
    alias: "呼叫点 / 点数",
    definition:
      "Call Credit 是 CALL ANYTHING 当前阶段使用的内部计费单位。Caller 充值进余额 → 调用扣减 → Responder 服务被调用收入累积成同一单位。同一 user 账户 caller 端余额与 responder 端收入合并：你赚的点数立刻就是你能花的余额，不用 transfer。当前阶段不开放真实货币提现。",
    context:
      "Call Credit 是协议层抽象——平台暂用点数体系，不直接经手法币、不接入支付商。是否接入法币是后续阶段决策，与协议契约无关。",
    relatedIds: ["pricing-models", "auto-refund", "trust-tier"],
    group: "billing-governance",
  },
  {
    id: "pricing-models",
    term: "三种定价模式",
    alias: "fixed_price / base_plus_duration / base_plus_tokens",
    definition:
      "Hotline 必须在 fixed_price（一口价）/ base_plus_duration（基础价 + 实际时长）/ base_plus_tokens（基础价 + 自定义 unit）之中选一个，不允许混合。三者都是「价值定价」的形式——Caller 同意的是「这件事值多少」，不是「跑了多少 token」。Hotline 必声明硬上限作为 Caller 安全锚点。",
    context:
      "fixed_price 是无声明默认。base_plus_tokens 中的「token」是 Hotline 自定义语义，可以是 LLM token / 文档页数 / 图片张数等，不绑定 LLM provider 实际 unit。",
    relatedIds: ["hotline", "call-credit", "auto-refund"],
    group: "billing-governance",
  },
  {
    id: "auto-refund",
    term: "Auto-refund · 自动退款",
    alias: "协议层强制退款",
    definition:
      "由协议层自动触发的全额退款，无需 Caller 申诉。5 类机器可判定的失败：result 进入 UNVERIFIED（签名/schema 失败）、TIMED_OUT（hard_timeout）、不可重试 FAILED、Hotline 在 prepared 期间被 frozen、平台内容审查拒绝。退款直接回滚到余额。",
    context:
      "主观不满意走人工 dispute 队列，仅 untrusted 与 trusted tier hotline 受理；verified hotline 不受单边申诉以防滥用。Caller 单边申诉率 30 天 > 10% 时自动暂停申诉权。",
    relatedIds: ["call-credit", "trust-tier", "result-package"],
    group: "billing-governance",
  },
  {
    id: "trust-tier",
    term: "Trust Tier · 信任分级",
    alias: "Hotline 行为信任阶梯",
    definition:
      "每根 Hotline 的协议层信任档位，分四档：untrusted（默认初始）/ trusted / verified / frozen。决定单次金额上限、自动退款规则、是否受单边申诉、收入落账延迟。新提交 Hotline 即使过审也默认 untrusted；通过累积可签名指标 + 平台内容安全审查后才能升级。",
    context:
      "trust_tier 与 admin review 的 review_status 是正交维度——前者决定「上架后能收多少钱、走多严的退款规则」，后者决定「是否出现在目录」。零信任的核心：没有「approved → 直接 verified」捷径。",
    relatedIds: ["auto-refund", "pricing-models", "responder-responsibility"],
    group: "billing-governance",
  },
  {
    id: "responder-responsibility",
    term: "Responder 责任承担",
    alias: "OPC 作为经营者",
    definition:
      "OPC 选什么 LLM / 工具 / 流程是它的事，结果好坏也是它的事。LLM 抽风导致 result 进 UNVERIFIED → 自动全额退（OPC 损失收入）；LLM 长期 drift 触发熔断 → 降级（OPC 损失收费上限）。LLM 是 OPC 选用的工具，不是免责事由。协议层不区分「主观恶意」与「模型不可控输出」。",
    context:
      "「我不管你用什么手段，做完按约定付」（Caller 视角）的对偶面是「你用了什么手段，你为它的输出负责」（Responder 视角）。这是经营者角色定义。",
    relatedIds: ["pricing-models", "auto-refund", "trust-tier"],
    group: "billing-governance",
  },
];

export const GLOSSARY_GROUPS: Array<{
  id: GlossaryTerm["group"];
  label: string;
  intro: string;
}> = [
  {
    id: "core",
    label: "核心概念",
    intro: "理解 CALL ANYTHING 必备的 4 个词。",
  },
  {
    id: "role",
    label: "协议角色",
    intro: "Caller 与 Responder——一切调用的两端。",
  },
  {
    id: "marketplace",
    label: "Marketplace",
    intro: "公开 Hotline 目录如何承载发现与匹配。",
  },
  {
    id: "engineering",
    label: "工程化术语",
    intro: "客户端实现层的几个常见词，写代码时会反复看到。",
  },
  {
    id: "console",
    label: "Console 与运营",
    intro: "Caller Console / Responder Console / 审批 —— 协议运行时的管控面。",
  },
  {
    id: "billing-governance",
    label: "计费与治理",
    intro: "点数体系、三种定价模式、自动退款、信任分级 —— 协议层的「做完按约定付」是怎么实现的。",
  },
];

export function getGlossaryTermById(id: string): GlossaryTerm | undefined {
  return GLOSSARY_TERMS.find((term) => term.id === id);
}
